import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { Socket } from "socket.io-client";
import AcedModal from "../../components/essentials/modals/aced-modal/AcedModal";
import { Search } from "../../components/header/SearchIcon";
import { selectUser, setUser } from "../../core/auth/authSlice";
import {
  addMessage,
  editLastMessage,
  removeLastMessage,
} from "../../core/auth/chatSlice";
import { User } from "../../shared/models";
import { useWebSocket } from "../../shared/socket-provider/WebSocketProvider";
import ChatList from "./chat-list/ChatList";
import { IMessage } from "./components/message/Message";
import "./Messages.scss";

const Messages: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser) as User;
  const location = useLocation();

  const freeSocket = (socket: Socket | undefined) => {
    socket?.off("newMessage");
  };

  const socket = useWebSocket();

  useEffect(() => {
    if (!socket) {
      console.warn("Messages: Socket not available");
      return;
    }

    console.log("Messages: Setting up socket listeners, connected:", socket.connected);

    socket.on("newMessage", (newMessage: IMessage) => {
      // For system messages, only show to the receiver
      if (newMessage.type === "system") {
        if (newMessage.receiver === user?.actorId) {
          console.log("Messages: Received system message", newMessage);
          dispatch(
            addMessage({
              chatId: newMessage.chatId,
              message: newMessage,
            })
          );
        }
      } else if (newMessage.sender !== user?.actorId) {
        // For regular messages, show if not sent by current user
        console.log("Messages: Received new message", newMessage);
        dispatch(
          addMessage({
            chatId: newMessage.chatId,
            message: newMessage,
          })
        );
      }
    });

    socket.on("editLastMessage", (editedMessage) => {
      console.log("Messages: Edit last message", editedMessage);
      dispatch(editLastMessage(editedMessage));
    });

    socket.on("deleteLastMessage", (payload) => {
      const { chatId, messageId } = payload;
      console.log("Messages: Delete last message", payload);
      dispatch(removeLastMessage({ chatId, messageId }));
    });

    socket.on("disconnect", (reason) => {
      console.log("Messages: Disconnected. Reason:", reason);
    });

    socket.on("connect", () => {
      console.log("Messages: Socket connected");
    });

    socket.on("connect_error", (error) => {
      console.error("Messages: Connection error:", error);
    });

    return () => {
      if (socket) freeSocket(socket);
    };
  }, [socket, user?.actorId, dispatch]);

  const isChatOpen = location.pathname !== "/messages";

  return (
    <div className="messages__container">
      <div className={`chatList-outlet ${isChatOpen ? "chatList-outlet--hidden" : ""}`}>
        <ChatList />
      </div>
      <Outlet />
    </div>
  );
};

export default Messages;

export const EmptyChat: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <div className="empty-chat">
      <h1>{t("messages.yourMessages")}</h1>
      <p>{t("messages.selectChatPrompt")}</p>
      <p>{t("messages.or")}</p>
      <button
        className="btn btn-primary"
        onClick={() => {
          setModalOpen(true);
        }}
      >
        {t("common.buttons.startNewChat")}
      </button>
      <NewMessageModal
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
      ></NewMessageModal>
    </div>
  );
};

export interface NewMessageModalProps {
  isModalOpen: any;
  setModalOpen: any;
}
export const NewMessageModal: React.FC<NewMessageModalProps> = ({
  isModalOpen,
  setModalOpen,
}) => {
  return (
    <>
      <AcedModal
        state={{ isModalOpen, setModalOpen }}
        className="new-message-modal"
        title="New Message"
      >
        <Search action="message" onClose={() => setModalOpen(false)} />
      </AcedModal>
    </>
  );
};
